import StateDisplay from "@/src/components/ui-kit/state-display";
import { useAllWikiFeedback } from "@/src/services/wiki/wiki-feedback.hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import FeedbackCard from "./feedback-card";
import { FeedbackListSkeleton } from "./feedback-card-skeleton";

const EmptyState = () => (
  <StateDisplay
    descriptionLabel="LABEL_NO_FEEDBACK_YET"
    imgSource={require("@/src/assets/images/empty.png")}
  />
);

type FeedbackFilter = "pending" | "resolved";

/**
 * Admin-only list of every feedback submission for the wiki as a whole,
 * newest first. Plain `.map()` inside a `View`, not a `FlatList` — this panel
 * renders inside the wiki screen's existing scrollable content area, and
 * nesting a same-orientation virtualized list would throw.
 *
 * Segregated into Pending/Resolved, mirroring the Unread/All pill toggle on
 * the Pinned News tab. A row with no `resolved` value yet (submitted before
 * this field existed) counts as Pending, not Resolved.
 */
export default function WikiFeedbackList() {
  const { t } = useTranslation();
  const { data: feedback = [], isLoading } = useAllWikiFeedback();
  const [filter, setFilter] = useState<FeedbackFilter>("pending");

  const pending = feedback.filter((item) => item.resolved !== true);
  const resolved = feedback.filter((item) => item.resolved === true);
  const visible = filter === "pending" ? pending : resolved;

  if (isLoading) {
    return <FeedbackListSkeleton variant="admin" />;
  }

  return (
    <View className="gap-3">
      <View className="flex-row gap-1">
        {([
          { key: "pending" as const, label: "LABEL_PENDING", count: pending.length },
          { key: "resolved" as const, label: "LABEL_RESOLVED", count: resolved.length },
        ]).map(({ key, label, count }) => {
          const isActive = filter === key;
          return (
            <Pressable
              key={key}
              onPress={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full ${
                isActive
                  ? "bg-primary-2 border border-primary"
                  : "border border-ash-3 hover:bg-ash-3"
              }`}
            >
              <Text
                className={`text-xs font-medium ${
                  isActive ? "text-primary" : "text-ash-1"
                }`}
              >
                {t(label)} ({count})
              </Text>
            </Pressable>
          );
        })}
      </View>

      {visible.length === 0 ? (
        <EmptyState />
      ) : (
        <View className="gap-2">
          {visible.map((item) => (
            <FeedbackCard key={item.ItemId} feedback={item} />
          ))}
        </View>
      )}
    </View>
  );
}
